import { KeyboardEvent, useRef, useState } from 'react';
import { useRachelChatContext } from '../../contexts/Rachel.context';
import { useRachelAttachmentsContext } from '../../contexts/RachelAttachments.context';
import { useRachelPageNavigation } from '../../hooks/useRachelPageNavigation';
import { UseRachelSubmitReturn } from '../../hooks/useRachelSubmit.types';
import { registerRachelInputFocus } from '../../rachel.inputFocus';
import { RachelChatStatus } from '../../rachel.types';
import rachelStore from '../../store/rachel.store';
import { useRachelInputMultiline } from './useRachelInputMultiline';

export interface UseRachelInputParams {
  rachelSubmit: UseRachelSubmitReturn;
  shouldOpenOnFocus?: boolean;
  shouldOpenPageOnSend?: boolean;
  shouldPublishFocus?: boolean;
}

export const useRachelInput = ({
  rachelSubmit,
  shouldOpenOnFocus = true,
  shouldOpenPageOnSend = false,
  shouldPublishFocus = false,
}: UseRachelInputParams) => {
  const { submit, isSubmitting } = rachelSubmit;
  const { stop, status, isStreaming } = useRachelChatContext();
  const { attachments, consumeReadyParts, hasBlockingAttachment } = useRachelAttachmentsContext();
  const { openPage } = useRachelPageNavigation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState('');
  const { isMultiline, measureRef } = useRachelInputMultiline({ inputRef, value });
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const setInputNode = (node: HTMLInputElement | null) => {
    measureRef(node);

    if (shouldPublishFocus) {
      registerRachelInputFocus(node && (() => node.focus()));
    }
  };

  const publishFocus = (isInputFocused: boolean) => {
    if (shouldPublishFocus) {
      rachelStore.setIsInputFocused(isInputFocused);
    }
  };

  const isBusy = status === RachelChatStatus.SUBMITTED || status === RachelChatStatus.STREAMING;
  const hasText = !!value.trim();
  const isActive = isHovered || isFocused || hasText || isStreaming;

  const finishSuccessfulSend = (sentText: string) => {
    setValue((currentValue) => (currentValue.trim() === sentText ? '' : currentValue));
    if (shouldOpenPageOnSend) {
      inputRef.current?.blur();
      openPage();
    }
  };

  const handleSend = async () => {
    const text = value.trim();
    const isSendBlocked = [isBusy, isSubmitting, hasBlockingAttachment].some(Boolean);
    if (isSendBlocked) {
      return;
    }

    const readyAttachmentIds = attachments
      .filter((attachment) => attachment.status === 'ready')
      .map((attachment) => attachment.id);
    if (!text && readyAttachmentIds.length === 0) {
      return;
    }

    const wasSent = await submit(text, () => consumeReadyParts(readyAttachmentIds));
    if (!wasSent) {
      return;
    }

    finishSuccessfulSend(text);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    publishFocus(true);

    if (shouldOpenOnFocus) {
      rachelStore.open();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    publishFocus(false);
  };

  return {
    inputRef,
    value,
    setValue,
    hasText,
    isActive,
    isMultiline,
    isStreaming,
    measureRef: setInputNode,
    setIsHovered,
    stop,
    handleSend,
    handleKeyDown,
    handleFocus,
    handleBlur,
  };
};
